"use client";

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Feature from '@/components/Feature';
import { useUser, SignInButton } from '@clerk/nextjs';
import { useCartContext } from '@/context/CartContext';
import { useToast } from '@/context/ToastContext';

const CheckOutPage = () => {
  const { isSignedIn, isLoaded } = useUser();
  const { cart, cartTotal, clearCart } = useCartContext();
  const router = useRouter();
  const { showToast } = useToast();

  const [formData, setFormData] = React.useState({
    firstName: '',
    lastName: '',
    country: 'Pakistan',
    streetAddress: '',
    city: '',
    province: 'Punjab',
    zipCode: '',
    phone: '',
    email: '',
  });

  const [shippingCost, setShippingCost] = React.useState<number | null>(null);
  const [baseShippingCharge, setBaseShippingCharge] = React.useState(0);
  const [shippingDiscount, setShippingDiscount] = React.useState(0);
  const [isFreeShipping, setIsFreeShipping] = React.useState(false);
  const [deliveryTime, setDeliveryTime] = React.useState('');
  const [shippingThreshold, setShippingThreshold] = React.useState<number | null>(null);

  const PAKISTANI_PROVINCES = [
    "Punjab",
    "Sindh",
    "Khyber Pakhtunkhwa",
    "Balochistan",
    "Islamabad Capital Territory",
    "Azad Kashmir",
    "Gilgit-Baltistan"
  ];

  const CITIES_BY_PROVINCE: Record<string, string[]> = {
    "Punjab": [
      "Lahore", "Faisalabad", "Rawalpindi", "Gujranwala", "Multan", "Sargodha", "Sialkot", "Bahawalpur", 
      "Gujrat", "Sahiwal", "Wah Cantonment", "Kasur", "Okara", "Chiniot", "Hafizabad", "Sadiqabad", 
      "Burewala", "Khanewal", "Muzaffargarh", "Muridke", "Jhelum", "Rahim Yar Khan", "Sheikhupura", "Dera Ghazi Khan"
    ].sort(),
    "Sindh": [
      "Karachi", "Hyderabad", "Sukkur", "Larkana", "Nawabshah", "Kotri", "Mirpur Khas", "Shikarpur", "Jacobabad"
    ].sort(),
    "Khyber Pakhtunkhwa": [
      "Peshawar", "Mardan", "Mingora", "Kohat", "Abbottabad"
    ].sort(),
    "Balochistan": [
      "Quetta"
    ].sort(),
    "Islamabad Capital Territory": [
      "Islamabad"
    ],
    "Azad Kashmir": [
      "Muzaffarabad"
    ],
    "Gilgit-Baltistan": [
      "Gilgit", "Skardu"
    ].sort()
  };

  const [paymentMethod, setPaymentMethod] = React.useState('Cash On Delivery');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [payfastPayload, setPayfastPayload] = React.useState<Record<string, string> | null>(null);
  const [isPayFastAvailable, setIsPayFastAvailable] = React.useState(true);
  const payfastFormRef = React.useRef<HTMLFormElement>(null);

  React.useEffect(() => {
    const checkConfig = async () => {
      try {
        const res = await fetch('/api/shipping/settings');
        const data = await res.json();
        setIsPayFastAvailable(!!data.isPayFastConfigured);
      } catch (err) {
        console.warn("Failed to check configuration settings", err);
        setIsPayFastAvailable(false);
      }
    };
    checkConfig();
  }, []);

  React.useEffect(() => {
    if (payfastPayload && payfastFormRef.current) {
        payfastFormRef.current.submit();
    }
  }, [payfastPayload]);

  // Calculate Shipping
  React.useEffect(() => {
    const calculateShipping = async () => {
      try {
        const res = await fetch('/api/shipping/calculate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cartTotal, city: formData.city, province: formData.province }),
        });
        const data = await res.json();
        if (data.shippingCost !== undefined) {
          setShippingCost(data.shippingCost);
          setBaseShippingCharge(data.baseCharge || 0);
          setShippingDiscount(data.discount || 0);
          setIsFreeShipping(data.isFree || false);
          setDeliveryTime(data.deliveryTime);
          setShippingThreshold(data.threshold);
        }
      } catch (err) {
        console.error("Failed to calculate shipping", err);
      }
    };

    calculateShipping();
  }, [formData.city, formData.province, cartTotal]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const nextData = { ...prev, [name]: value };
      if (name === 'province') {
        const allowedCities = CITIES_BY_PROVINCE[value] || [];
        if (!allowedCities.includes(prev.city)) {
          nextData.city = '';
        }
      }
      return nextData;
    });
  };

  const handlePlaceOrder = async () => {
    if (!formData.firstName || !formData.email || !formData.phone || !formData.streetAddress || !formData.city || !formData.province) {
      showToast("Please fill in all required fields.", "warning");
      return;
    }

    setIsSubmitting(true);

    // Generate unique idempotency key to prevent double checkout clicks or network retry duplicates
    const idempotencyKey = crypto.randomUUID();

    try {
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            address: `${formData.streetAddress}, ${formData.city}, ${formData.province}`,
            city: formData.city,
            zipCode: formData.zipCode,
          },
          items: cart,
          totalPrice: cartTotal + (shippingCost || 0),
          shippingCost: shippingCost || 0,
          paymentMethod: paymentMethod,
          idempotencyKey: idempotencyKey,
        }),
      });

      const data = await response.json();

      if (data.success) {
        if (paymentMethod === 'PayFast') {
          // Initiate PayFast
          const payfastResponse = await fetch('/api/payfast/initiate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amount: cartTotal + (shippingCost || 0),
              orderId: data.orderId,
              customerData: formData,
            }),
          });
          const payfastData = await payfastResponse.json();
          if (payfastData.success && payfastData.payload) {
             setPayfastPayload(payfastData.payload);
             return; // State update triggers form submission effect
          } else {
             showToast('Failed to initialize PayFast payment.', 'error');
             setIsSubmitting(false);
          }
        } else {
          showToast("Order placed successfully! Redirecting...", "success", 3000);
          clearCart();
          router.push(`/checkout/success?order_id=${data.orderId}`);
        }
      } else {
        showToast("Failed to place order: " + data.error, "error");
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error(error);
      showToast("An error occurred while placing your order.", "error");
      setIsSubmitting(false);
    }
  };

  // Redirect to cart if cart is empty
  React.useEffect(() => {
    if (cart.length === 0 && !isSubmitting) {
      router.push('/cart');
    }
  }, [cart, router, isSubmitting]);

  // Show sign-in prompt if user is not signed in
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B88E2F]"></div>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <h1 className="text-2xl font-bold mb-4">Sign In Required</h1>
          <p className="text-gray-600 mb-6">
            Please sign in to proceed with your purchase and complete your order.
          </p>
          <SignInButton mode="modal">
            <button className="w-full bg-[#B88E2F] hover:bg-[#a37d2a] text-white font-bold py-3 px-4 rounded transition">
              Sign In to Continue
            </button>
          </SignInButton>
          <p className="text-sm text-gray-500 mt-4">
            Don't have an account? Sign up to create one
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-28">
        {/* Elegant Minimal Header */}
        <div className="mb-6 text-left border-b border-gray-100 pb-4">
          <div className="flex items-center gap-2 text-xs font-bold tracking-wider text-[#B88E2F] uppercase mb-1">
            <Link href="/" className="hover:text-gray-950 transition-colors">Home</Link>
            <span className="text-gray-300">/</span>
            <Link href="/cart" className="hover:text-gray-950 transition-colors">Cart</Link>
            <span className="text-gray-300">/</span>
            <span className="text-gray-400">Checkout</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            Checkout
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1.5 leading-relaxed">
            Please fill in your billing details to complete your order.
          </p>
        </div>
      </div>
      <div className="container mx-auto px-4 lg:px-12 mt-6">
        <div className="flex flex-col lg:flex-row items-start justify-between gap-10">
          {/* Left Side: Billing Form */}
          <div className="w-full lg:w-[60%]">
            <h1 className="text-xl font-semibold mb-6">Billing Details</h1>

            {/* Contact Information */}
            <div className="mb-6">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 pb-2 border-b border-gray-100">
                Contact Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    type="text"
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#B88E2F]/40 focus:border-[#B88E2F] transition"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    type="text"
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#B88E2F]/40 focus:border-[#B88E2F] transition"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    type="email"
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#B88E2F]/40 focus:border-[#B88E2F] transition"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    type="tel"
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#B88E2F]/40 focus:border-[#B88E2F] transition"
                  />
                </div>
              </div>
            </div>

            {/* Delivery Details */}
            <div className="mb-6">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 pb-2 border-b border-gray-100">
                Delivery Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label htmlFor="streetAddress" className="block text-sm font-medium text-gray-700 mb-1">
                    Street Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="streetAddress"
                    name="streetAddress"
                    value={formData.streetAddress}
                    onChange={handleInputChange}
                    type="text"
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#B88E2F]/40 focus:border-[#B88E2F] transition"
                  />
                </div>
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                    Town / City <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#B88E2F]/40 focus:border-[#B88E2F] transition bg-white"
                  >
                    <option value="">Select City</option>
                    {(CITIES_BY_PROVINCE[formData.province] || []).map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-1">
                    Province <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="province"
                    name="province"
                    value={formData.province}
                    onChange={handleInputChange}
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#B88E2F]/40 focus:border-[#B88E2F] transition bg-white"
                  >
                    {PAKISTANI_PROVINCES.map(province => (
                      <option key={province} value={province}>{province}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                    Country / Region
                  </label>
                  <input
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    type="text"
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#B88E2F]/40 focus:border-[#B88E2F] transition"
                  />
                </div>
                <div>
                  <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Code
                  </label>
                  <input
                    id="zipCode"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    type="text"
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#B88E2F]/40 focus:border-[#B88E2F] transition"
                  />
                </div>
              </div>
            </div>

          </div>

          {/* Right Side: Order Summary */}
          <div className="w-full lg:w-[35%]">
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 border-b border-gray-100 pb-4 mb-4">Your Order</h2>
              
              {/* Product list */}
              <div className="divide-y divide-gray-100 mb-6 max-h-[300px] overflow-y-auto pr-2">
                {cart.map((item) => (
                  <div key={item._id} className="py-3 flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 leading-snug">
                        {item.title} <span className="text-[#B88E2F] font-bold">× {item.quantity}</span>
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 flex-shrink-0">
                      Rs. {(item.price * item.quantity).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-3 text-sm border-t border-gray-100 pt-4">
                <div className="flex justify-between items-center text-gray-600">
                  <span>Subtotal</span>
                  <span className="font-semibold text-gray-900">Rs. {cartTotal.toLocaleString()}</span>
                </div>
                
                <div className="flex justify-between items-start text-gray-600">
                  <span>Shipping</span>
                  <div className="text-right">
                    <span className={`font-semibold ${shippingCost === 0 ? 'text-green-600' : 'text-gray-900'}`}>
                      {shippingCost === null ? (
                        <span className="text-xs text-gray-400 animate-pulse font-normal">Calculating...</span>
                      ) : (isFreeShipping && shippingCost === 0) ? (
                        'FREE'
                      ) : (
                        `Rs. ${shippingCost.toLocaleString()}`
                      )}
                    </span>
                    {deliveryTime && <div className="text-[10px] text-gray-400 mt-0.5">Est. {deliveryTime}</div>}
                  </div>
                </div>

                {shippingCost !== null && shippingCost > 0 && shippingDiscount > 0 && (
                  <div className="flex justify-between items-center text-green-600">
                    <span>Shipping Discount</span>
                    <span className="font-medium">- Rs. {shippingDiscount.toLocaleString()}</span>
                  </div>
                )}

                <div className="border-t border-gray-200 pt-3 mt-3 flex justify-between items-center text-gray-900">
                  <span className="font-bold text-base">Total</span>
                  <span className="text-[#B88E2F] text-xl font-extrabold">
                    Rs. {(cartTotal + (shippingCost || 0)).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-8 space-y-4">
              {/* Cash On Delivery */}
              <div className="flex flex-col gap-2">
                <div
                  className="flex items-center gap-3 cursor-pointer"
                  onClick={() => setPaymentMethod('Cash On Delivery')}
                >
                  <div className={`rounded-full flex-shrink-0 w-[14px] h-[14px] ${paymentMethod === 'Cash On Delivery' ? 'bg-black' : 'border border-[#9F9F9F]'}`}></div>
                  <h1 className={`text-[18px] font-semibold ${paymentMethod === 'Cash On Delivery' ? 'text-black' : 'text-[#333333]'}`}>Cash On Delivery</h1>
                </div>
                {paymentMethod === 'Cash On Delivery' && (
                  <p className="text-[#333333] pl-6 text-sm">
                    Pay with cash upon delivery.
                  </p>
                )}
              </div>

              {/* Direct Bank Transfer */}
              <div className="flex flex-col gap-2">
                <div
                  className="flex items-center gap-3 cursor-pointer"
                  onClick={() => setPaymentMethod('Bank Transfer')}
                >
                  <div className={`rounded-full flex-shrink-0 w-[14px] h-[14px] ${paymentMethod === 'Bank Transfer' ? 'bg-black' : 'border border-[#9F9F9F]'}`}></div>
                  <h1 className={`text-[18px] font-semibold ${paymentMethod === 'Bank Transfer' ? 'text-black' : 'text-[#333333]'}`}>Direct Bank Transfer</h1>
                </div>
                {paymentMethod === 'Bank Transfer' && (
                  <div className="text-[#333333] pl-6 text-sm bg-[#FAF8F5] p-4 rounded-xl border border-gray-200/50 space-y-2 mt-1">
                    <p className="font-semibold text-gray-700">Please transfer the order amount to the following bank account:</p>
                    <div className="grid grid-cols-3 gap-y-1.5 text-xs text-gray-600 font-medium">
                      <span>Bank Name:</span>
                      <span className="col-span-2 font-bold text-gray-900">Bank AL Habib Limited (BAHL)</span>

                      <span>Account Name:</span>
                      <span className="col-span-2 font-bold text-gray-900">usman kalil</span>

                      <span>IBAN:</span>
                      <span className="col-span-2 font-bold text-gray-900 select-all font-mono">PK19BAHL1278008103605101</span>
                    </div>
                    <p className="text-[11px] text-gray-500 italic mt-2">Note: Please take a screenshot of your transfer receipt for order verification.</p>
                  </div>
                )}
              </div>

              {/* PayFast */}
              <div className="flex flex-col gap-2">
                <div
                  className={`flex items-center gap-3 ${isPayFastAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                  onClick={() => isPayFastAvailable && setPaymentMethod('PayFast')}
                >
                  <div className={`rounded-full flex-shrink-0 w-[14px] h-[14px] ${paymentMethod === 'PayFast' ? 'bg-black' : 'border border-[#9F9F9F]'}`}></div>
                  <h1 className={`text-[18px] font-semibold ${paymentMethod === 'PayFast' ? 'text-black' : 'text-[#333333]'}`}>
                    PayFast {!isPayFastAvailable && <span className="text-xs font-normal text-red-500">(Temporarily Unavailable)</span>}
                  </h1>
                </div>
                {paymentMethod === 'PayFast' && isPayFastAvailable && (
                  <p className="text-[#333333] pl-6 text-sm">
                    Pay securely using PayFast module.
                  </p>
                )}
                {!isPayFastAvailable && (
                   <p className="text-red-500 pl-6 text-xs italic">
                     Online payment is currently unavailable. Please use Cash on Delivery or Bank Transfer.
                   </p>
                )}
              </div>
            </div>
            <div className="mt-10">
              <button
                disabled={isSubmitting}
                onClick={handlePlaceOrder}
                className="w-full lg:w-[318px] h-[64px] border border-black rounded-2xl hover:bg-black hover:text-white transition disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Placing Order...' : 'Place order'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {payfastPayload && (
        <form
          ref={payfastFormRef}
          action={process.env.NEXT_PUBLIC_PAYFAST_MODE === 'live' ? 'https://www.payfast.co.za/eng/process' : 'https://sandbox.payfast.co.za/eng/process'}
          method="POST"
          style={{ display: 'none' }}
        >
          {Object.entries(payfastPayload).map(([key, value]) => (
            <input key={key} type="hidden" name={key} value={value as string} />
          ))}
        </form>
      )}

      <Feature />
    </>
  );
};

export default CheckOutPage;
